/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { SnippetsSectionComponent } from '../../components/SnippetsContainer/Snippets/types';
import type { getSnippetsSectionsOptions } from './types';

import { Css } from '../../components/SnippetsContainer/Sections/Css';
import { Documentation } from '../../components/SnippetsContainer/Sections/Documentation';
import { DownloadProject } from '../../components/SnippetsContainer/Sections/DownloadProject';
import { EditInStackBlitz } from '../../components/SnippetsContainer/Sections/EditInStackBlitz';
import { Explore } from '../../components/SnippetsContainer/Sections/Explore';
import { Feedback } from '../../components/SnippetsContainer/Sections/Feedback';
import { Html } from '../../components/SnippetsContainer/Sections/Html';
import { Install } from '../../components/SnippetsContainer/Sections/Install';
import { Js } from '../../components/SnippetsContainer/Sections/Js';
import { JsZipDownload } from '../../components/SnippetsContainer/Sections/JsZipDownload';
import { NpmSetupWithCkbox } from '../../components/SnippetsContainer/Sections/NpmSetupWithCkbox';
import { Run } from '../../components/SnippetsContainer/Sections/Run';
import { SnippetsPremiumSection } from '../../components/SnippetsContainer/Sections/SnippetsPremiumSection';
import { SnippetsTechnologySection } from '../../components/SnippetsContainer/Sections/SnippetsTechnologySection';
import { SnippetsWelcomeSection } from '../../components/SnippetsContainer/Sections/SnippetsWelcomeSection';
import { VueMainJs } from '../../components/SnippetsContainer/Sections/VueMainJs';

export function getSnippetsSections( {
	hasPremiumPlugins,
	editorPlugins,
	selectedTechnology,
	selectedInstallMethod,
	selectedCodeOutput,
	isLoggedIn
}: getSnippetsSectionsOptions ): Array<SnippetsSectionComponent> {
	const isJs = selectedTechnology === 'js';
	const isVue = selectedTechnology === 'vue';
	const isAngular = selectedTechnology === 'angular';
	const isCdn = selectedInstallMethod === 'cdn';
	const isNpm = selectedInstallMethod === 'npm';
	const isZip = selectedInstallMethod === 'zip';
	const isSnippets = selectedCodeOutput === 'snippets';
	const isStackblitz = selectedCodeOutput === 'stackblitz';
	const isDownload = selectedCodeOutput === 'download';
	const hasCKBox = editorPlugins.includes( 'CKBox' );

	return [
		SnippetsWelcomeSection,
		SnippetsPremiumSection,
		SnippetsTechnologySection,
		insertIf( isSnippets, ...[
			insertIf( isNpm || ( isCdn && !isJs ), Install ),
			insertIf( isZip, JsZipDownload ),
			insertIf( !isJs && isNpm && hasCKBox, NpmSetupWithCkbox ),
			insertIf( isJs || isAngular, Html ),
			Css,
			insertIf( isVue, VueMainJs ),
			Js,
			Run
		].flat() ),
		insertIf( isStackblitz, EditInStackBlitz ),
		insertIf( isDownload, DownloadProject ),
		insertIf( isDownload || ( ( isStackblitz && !isLoggedIn ) && !hasPremiumPlugins ), Explore ),
		insertIf( !!selectedCodeOutput && !( isStackblitz && hasPremiumPlugins ), Documentation, Feedback )
	].flat();
}

function insertIf( condition: boolean, ...elements: Array<SnippetsSectionComponent> ): Array<SnippetsSectionComponent> {
	return condition ? elements : [];
}
